// Synthesized UI sounds via Web Audio — no asset files needed.
// A single shared AudioContext, resumed lazily (browsers require a prior user
// gesture; agents have already clicked to log in, so this is fine on the
// dashboard).

let ctx = null;
function audioCtx() {
  if (typeof window === "undefined") return null;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

// Warm up / unlock audio on the first user gesture so later sounds are instant.
export function primeAudio() {
  audioCtx();
}

// Polished "new message" notification: a soft bell arpeggio (C6 · E6 · G6)
// with an octave overtone and a gentle decay — bright, friendly and distinct
// from the ring.
let lastDing = 0;
export function playMessageDing() {
  const c = audioCtx();
  if (!c) return;
  // Debounce bursts so a flood of messages doesn't machine-gun the sound.
  const now = Date.now();
  if (now - lastDing < 350) return;
  lastDing = now;

  const t = c.currentTime;
  const bell = (freq, start, dur, peak) => {
    // fundamental + soft octave for a bell-like timbre
    [[freq, 1], [freq * 2, 0.3]].forEach(([f, mul]) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + start);
      g.gain.exponentialRampToValueAtTime(peak * mul, t + start + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, t + start + dur);
      o.connect(g);
      g.connect(c.destination);
      o.start(t + start);
      o.stop(t + start + dur + 0.02);
    });
  };
  bell(1046, 0, 0.5, 0.16);     // C6
  bell(1318, 0.08, 0.5, 0.15);  // E6
  bell(1568, 0.16, 0.6, 0.17);  // G6
}

// Bold telephone-style ring: a dual-tone (440 + 480 Hz) double-ring burst,
// repeated on an interval. Returns { start, stop }.
export function createRingtone() {
  let timer = null;

  const ringOnce = () => {
    const c = audioCtx();
    if (!c) return;
    const t0 = c.currentTime;
    const master = c.createGain();
    master.connect(c.destination);
    master.gain.setValueAtTime(0.0001, t0);

    const o1 = c.createOscillator();
    const o2 = c.createOscillator();
    o1.type = "sine";
    o2.type = "sine";
    o1.frequency.value = 440;
    o2.frequency.value = 480;
    o1.connect(master);
    o2.connect(master);

    // Two 0.4s rings, 0.2s apart — the classic "ring ring … " cadence, loud.
    const seg = (start) => {
      const g = master.gain;
      g.setValueAtTime(0.0001, t0 + start);
      g.exponentialRampToValueAtTime(0.34, t0 + start + 0.04);
      g.setValueAtTime(0.34, t0 + start + 0.36);
      g.exponentialRampToValueAtTime(0.0001, t0 + start + 0.4);
    };
    seg(0);
    seg(0.6);

    o1.start(t0);
    o2.start(t0);
    o1.stop(t0 + 1.1);
    o2.stop(t0 + 1.1);
  };

  return {
    start() {
      if (timer) return;
      ringOnce();
      timer = setInterval(ringOnce, 2400);
    },
    stop() {
      clearInterval(timer);
      timer = null;
    },
  };
}
